import { useContext, useEffect, useState } from 'react';
import { IOptionFilter, OptionsRequest, TApiEntity } from '@src/models/api';
import { IOption } from '@src/models/props';
import { ILegoElem } from '@src/molecules/Lego';
import { AppContext } from '../providers/App';
import { NgApiClient } from '@src/api/ngApiClient';
import { useFetch } from '@src/hooks/useFetch';

const hasEmptyValue = (items: Partial<IOptionFilter[]>): boolean => {
  if (!items) {
    return false;
  }

  for (const item of items.values()) {
    if (!item || item.value === null || item.value === undefined) {
      return true;
    }
  }
  return false;
};

const fetchOptions = (
  ngApiClient: NgApiClient,
  entityId: string | undefined,
  optionListId: string,
  optionsRequest: OptionsRequest,
) => {
  return ngApiClient.options(entityId as TApiEntity, optionListId, optionsRequest);
};

/**
 * Загрузить опшены для select-ов lego
 * Обрабатывают зависимые данные через `useLegoElemDependsOptionFilters`.
 * Например загрузить продукты выбранного ранее клиента и перезагрузить список, при перевыборе клиента.
 * Подробнее читай в `useLegoElemDependsOptionFilters`.
 */
export const useLoadOptions = (
  elem: ILegoElem,
  dependsEntries?: IOptionFilter[],
): IOption<string | number>[] | undefined => {
  const { ngApiClient } = useContext(AppContext);

  const [options, setOptions] = useState<IOption<string | number>[] | undefined>();

  const { result, fetch: fetchEntityOptionsCb } = useFetch({
    cb: fetchOptions,
  });

  useEffect(() => {
    if (elem.loadOptions?.entity) {
      const entityId = elem.loadOptions?.entity.entityId;
      const optionListId = elem.loadOptions?.entity.optionListId || 'default';
      const optionsRequest = elem.loadOptions?.entity.optionsRequest || { filters: [] };

      if (dependsEntries) {
        const filterDepends = dependsEntries.filter((e) => e.useInFilter);
        optionsRequest.filters.push(...filterDepends);

        const dependsLen = (elem.depends || []).length;
        if (dependsLen !== dependsEntries.length) {
          // В элементе могут быть указаны depends - это когда один, например select зависит
          // от выбранного значения другого select-а. В целом это необязательно должен быть именно select,
          // а может быть любой другой элемент (но пока у нас так умеет только select).
          // Тут мы выходит тогда, когда у элемента указаны зависимости, но они не пришли или не полностью пришли в dependsEntries
          // Например - элемент должен подгрузить данные только когда есть и clientId и productId. Если всего или чего-то
          // конкретного не пришло, то и грузить ничего не получится - бекенд вернет ошибку
          return;
        }
      }

      if (hasEmptyValue(optionsRequest.filters)) {
        return;
      }

      fetchEntityOptionsCb(ngApiClient, entityId, optionListId, optionsRequest);
    }
  }, [elem.depends, elem.loadOptions?.entity, ngApiClient, elem.id, dependsEntries, fetchEntityOptionsCb]);

  useEffect(() => {
    setOptions(
      result?.data.page.entries.map((option) => {
        return {
          value: option.id,
          label: option.name,
          img: option.logo,
          groupName: option?.section,
        };
      }),
    );
  }, [result]);

  return options;
};
